/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import "./playerProfile.scss";
import { getOverview, getProfile, getTeams } from "./api";
import { Box } from "./widgets";
import { IN, fiftyPlusSplit, battingStyleLabel, crestColor, dash, fx, humanize, initials, nz, seasonSeries } from "./model";
import OverviewPane, { useCareer } from "./OverviewPane";
import LeaguesSection from "./LeaguesSection";
import { buildLeagues } from "./leaguesModel";
import StatsPane from "./StatsPane";
import AnalysisPane from "./AnalysisPane";
import MatchesPane from "./MatchesPane";
import { RecordsPane, TeamsPane } from "./TeamsRecordsPanes";
import { decodeAuthTokenPayload, getAuthToken, getPlayerImageUrl } from "../../helper/helper";
import RoleCards from "./RoleCards";
import UserProfilePage from "../profile/UserProfilePage";
import { NEUTRAL_AVATAR } from "./neutralAvatar";

const PLAYER_TABS: [TabKey, string][] = [["stats", "Stats"], ["analysis", "Analysis"], ["matches", "Matches"], ["teams", "Teams"], ["records", "Records"]];
type TabKey = "overview" | "stats" | "analysis" | "matches" | "teams" | "records" | "myroles";
const ALL_TABS: TabKey[] = ["overview", "stats", "analysis", "matches", "teams", "records", "myroles"];

/**
 * Player profile. `self` = the signed-in user's own profile (/profile). The same page is used for everyone: it adapts to what the
 * person actually is (player with match data, or only a scorer / umpire / streamer) and, for the owner, adds Roles & Capabilities.
 */
const PlayerProfilePage = ({ self = false }: { self?: boolean }) => {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [params] = useSearchParams();
  const myId = useMemo(() => {
    const t = getAuthToken();
    const payload = t ? decodeAuthTokenPayload(t) : null;
    return (payload?.sub as string | undefined) ?? "";
  }, []);
  const playerId = useMemo(() => {
    if (self) return myId;
    const q = params.get("playerId");
    if (q) { sessionStorage.setItem("playerId", q); return q; }
    return sessionStorage.getItem("playerId") ?? "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, params, self, myId]);
  const isOwn = !!myId && playerId === myId;
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const [tab, setTab] = useState<TabKey>(() => (ALL_TABS.includes(location.hash.slice(1) as TabKey) ? (location.hash.slice(1) as TabKey) : "overview"));
  const [stuck, setStuck] = useState(false);
  const careerRef = useRef<HTMLDivElement>(null);

  const prof = useQuery({ queryKey: ["ppProfile", playerId], queryFn: () => getProfile(playerId), enabled: !!playerId });
  const ov = useQuery({ queryKey: ["ppOverview", playerId], queryFn: () => getOverview(playerId), enabled: !!playerId });
  const teamsQ = useQuery({ queryKey: ["ppTeams", playerId], queryFn: () => getTeams(playerId), enabled: !!playerId });

  const player = prof.data?.playerResponse;
  const overview = ov.data;
  const teams = teamsQ.data ?? [];
  const { bat, bowl, batAll, bowlAll, bbi } = useCareer(overview);

  const scorerProfile = prof.data?.scorerProfile;
  const streamerProfile = prof.data?.streamerProfile;
  const isScorer = !!scorerProfile?.active, isStreamer = !!streamerProfile?.active, isUmpire = !!player?.isUmpire;
  const hasOfficialRoles = isScorer || isStreamer || isUmpire;
  const dataLoaded = ov.isFetched && teamsQ.isFetched;
  // someone who only scores / umpires / streams has no match data: show them a profile built around their roles instead of empty stats
  const hasPlayerData = bat.length > 0 || bowl.length > 0 || nz<any>(overview?.recentMatches).length > 0 || teams.length > 0;
  const roleLabels = [isScorer && "Scorer", isUmpire && "Umpire", isStreamer && "Streamer"].filter(Boolean) as string[];
  const tabsList: [TabKey, string][] = [["overview", "Overview"], ...(dataLoaded && hasPlayerData ? PLAYER_TABS : []), ...(isOwn ? ([["myroles", "Roles & Capabilities"]] as [TabKey, string][]) : [])];
  const activeTab: TabKey = tabsList.some(([k]) => k === tab) ? tab : "overview";

  useEffect(() => {
    const el = careerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { rootMargin: "-130px 0px 0px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [player]);

  const go = (k: TabKey) => { setTab(k); window.history.replaceState(null, "", `${location.pathname}${location.search}#${k}`); };

  if (!playerId) return <div className="pp"><div className="wrap" style={{ padding: "80px 28px", textAlign: "center" }}>{self ? "Please sign in to see your profile." : "No player selected."}</div></div>;
  if (prof.isLoading) return <div className="pp"><div className="wrap" style={{ padding: "80px 28px", textAlign: "center" }}>Loading player profile…</div></div>;
  if (prof.isError || !player) return <div className="pp"><div className="wrap" style={{ padding: "80px 28px", textAlign: "center" }}>Unable to load player profile. Please try again.</div></div>;

  const fullName = [player.playerFirstName, player.playerLastName].filter(Boolean).join(" ");
  const image = getPlayerImageUrl(player.imageUri) || NEUTRAL_AVATAR;
  const leftHanded = /LEFT/i.test(player.battingStyle ?? "");
  const place = [player.city, player.state, player.country].filter(Boolean).join(", ");
  const age = player.dob ? Math.floor((Date.now() - new Date(player.dob).getTime()) / 31557600000) : null;
  const bowlStyle = humanize(player.bowlingStyle);
  const currentTeam = teams[0]?.teamName;

  const back = () => (returnTo ? nav(returnTo, { replace: true }) : nav(-1));
  const copyCode = () => { navigator.clipboard?.writeText(player.playerCode ?? "").catch(() => undefined); toast.success("Player code copied"); };
  const share = async () => {
    const url = `${window.location.origin}/tournaments/player-profile?playerId=${playerId}`;
    try { if (navigator.share) await navigator.share({ title: `${fullName} · CricCenter`, url }); else { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); } } catch { /* cancelled */ }
  };

  const hasBowl = bowlAll.w > 0 || bowlAll.balls > 0;
  const cn = (v: string | number, k: string, s?: string) => <div className="cnum" key={k}><div className="v">{v}</div><div className="k">{k}</div>{s && <div className="s">{s}</div>}</div>;
  const leagueGroups = buildLeagues(overview, teams);
  const tourCount = leagueGroups.reduce((a, l) => a + l.rows.length, 0);
  const season = seasonSeries(nz<any>(overview?.tournamentMiniStats), "all").filter((s) => s.m > 0).slice(-1)[0];
  const cell = (v: string | number, k: string) => <div key={k}><div className="v">{v}</div><div className="k">{k}</div></div>;
  const info: [string, ReactNode][] = [
    ["Full name", fullName], ["Age", age != null ? `${age} years` : dash],
    ["Location", place || dash], ["Role", humanize(player.playerRole) || dash],
    ["Batting style", battingStyleLabel(player.battingStyle) || dash], ["Bowling style", bowlStyle || dash],
    ["Current team", currentTeam ?? dash], ["Player code", player.playerCode ?? dash],
    ...(isOwn && player.dob ? ([["Date of birth", <>{new Date(player.dob).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}<small>Only you can see this</small></>]] as [string, ReactNode][]) : []),
  ];
  const showSeasonRail = hasPlayerData && !!season;
  const hasRail = showSeasonRail || teams.length > 0 || hasOfficialRoles;

  return (
    <>
    <div className="pp">
      <section className="hero bg-hex" id="hero">
        <div className="hbg" aria-hidden="true"><div className="opt opt-hex"><span className="halo" /><span className="tile" /></div><span className="scrim-l" /><span className="scrim-b" /><span className="grain" /></div>
        <div className="wrap">
          <nav className="crumb" aria-label="Breadcrumb"><a href="#" onClick={(e) => { e.preventDefault(); back(); }}>← Back</a><span>/</span><b>{fullName}</b></nav>
          <div className="hero-grid">
            <div className="photo"><img src={image} alt={fullName} onError={(e) => { if (e.currentTarget.src !== NEUTRAL_AVATAR) e.currentTarget.src = NEUTRAL_AVATAR; }} /></div>
            <div className="who">
              {currentTeam && <div className="team">{currentTeam}</div>}
              <h1>{fullName}</h1>
              <div className="idline">
                {player.playerCode && (<button className="code" onClick={copyCode} aria-label={`Copy player code ${player.playerCode}`}>Player code <b>{player.playerCode}</b><span className="cp"><svg width="12" height="12" viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="M11 5V3.5A1.5 1.5 0 009.5 2h-6A1.5 1.5 0 002 3.5v6A1.5 1.5 0 003.5 11H5" stroke="currentColor" strokeWidth="1.6" /></svg></span></button>)}
                <span className="style">{(hasPlayerData || !dataLoaded ? [humanize(player.playerRole), battingStyleLabel(player.battingStyle), bowlStyle] : roleLabels).filter(Boolean).map((s, i, a) => (<span key={s} style={{ display: "contents" }}>{s}{i < a.length - 1 && <i />}</span>))}</span>
              </div>
              {leagueGroups.length > 0 && (
                <button className="hero-meta" onClick={() => { go("overview"); document.getElementById("pp-leagues")?.scrollIntoView({ behavior: "smooth" }); }}>
                  <b>{leagueGroups.length}</b> league{leagueGroups.length === 1 ? "" : "s"}<i /><b>{tourCount}</b> tournament{tourCount === 1 ? "" : "s"}<i /><b>{teams.length}</b> team{teams.length === 1 ? "" : "s"}
                </button>
              )}
            </div>
            <div className="hacts">{isOwn && <button className="hbtn" onClick={() => go("myroles")}>Roles &amp; capabilities</button>}<button className="hbtn solid" onClick={share}>Share</button></div>
          </div>
          {hasPlayerData || !dataLoaded ? (
          <div className={`career ${hasBowl ? "" : "solo"}`} id="career" ref={careerRef}>
            <div className="cgroup"><div className="lbl">Batting, all formats</div><div className="nums" style={{ ["--n" as any]: 5 }}>
              {[cn(batAll.m, "Matches"), cn(IN(batAll.r), "Runs"), cn(fx(batAll.avg), "Average"), cn(batAll.h + batAll.f, "50+ scores", fiftyPlusSplit(batAll.h, batAll.f)), cn(batAll.hs, "Highest score")]}</div></div>
            {hasBowl && <div className="cgroup"><div className="lbl">Bowling</div><div className="nums" style={{ ["--n" as any]: 2 }}>{[cn(bowlAll.w, "Wickets"), cn(bbi ?? dash, "Best figures")]}</div></div>}
          </div>
          ) : (
          <div className="career solo" id="career" ref={careerRef}>
            <div className="cgroup"><div className="lbl">{hasOfficialRoles ? "Roles" : "Profile"}</div><div className="nums" style={{ ["--n" as any]: Math.max(roleLabels.length, 1) }}>
              {hasOfficialRoles ? [
                isScorer && cn(scorerProfile?.experienceYears ? `${scorerProfile.experienceYears} yrs` : "Active", "Scorer", scorerProfile?.verified ? "Verified" : undefined),
                isUmpire && cn(player.experience ? `${player.experience} yrs` : "Active", "Umpire"),
                isStreamer && cn(nz<any>(Array.isArray(streamerProfile?.streamingPlatforms) ? streamerProfile.streamingPlatforms : []).length || "Active", "Streamer", nz<any>(Array.isArray(streamerProfile?.streamingPlatforms) ? streamerProfile.streamingPlatforms : []).length ? "platforms" : undefined),
              ].filter(Boolean) : [cn("New", isOwn ? "Choose your roles to get started" : "No activity yet")]}</div></div>
          </div>
          )}
        </div>
      </section>
    </div>

    <div className="pp pp-sticky">
      <div className="tabsbar">
        <div className="wrap">
          <div className={`mini ${stuck ? "on" : ""}`} aria-hidden="true"><img src={image} alt="" onError={(e) => { if (e.currentTarget.src !== NEUTRAL_AVATAR) e.currentTarget.src = NEUTRAL_AVATAR; }} /><div><b>{fullName}</b><span>{humanize(player.playerRole)}</span></div></div>
          <div className="tabs" role="tablist" aria-label="Profile sections">
            {tabsList.map(([k, l]) => (<button key={k} className="tab" role="tab" id={`tab-${k}`} aria-selected={activeTab === k} tabIndex={activeTab === k ? 0 : -1} onClick={() => go(k)}>{l}</button>))}
          </div>
          <div className={`bar-acts ${stuck ? "on" : ""}`}><button className="btn primary" onClick={share}>Share</button></div>
        </div>
      </div>

    </div>

    {activeTab === "myroles" ? (
      <div className="pp-embed">
        <UserProfilePage rolesOnly onRolesSaved={() => { void queryClient.invalidateQueries({ queryKey: ["ppProfile", playerId] }); }} />
      </div>
    ) : (
    <div className="pp">
      <div className="wrap layout" style={hasRail ? undefined : { gridTemplateColumns: "minmax(0,1fr)" }}>
        <main>
          <section className="pane on" role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
            {activeTab === "overview" && (<>
              <Box><div className="info">{info.map(([k, v]) => (<div key={k}><div className="k">{k}</div><div className="v">{v}</div></div>))}</div></Box>
              {hasOfficialRoles && <RoleCards player={player} scorer={scorerProfile} streamer={streamerProfile} />}
              {!dataLoaded ? <Box><p className="acap" style={{ padding: 18 }}>Loading profile…</p></Box>
                : hasPlayerData ? (ov.isError || !overview ? <Box><p className="acap" style={{ padding: 18 }}>Career stats will appear here once available.</p></Box> : <><div id="pp-leagues"><LeaguesSection overview={overview} teams={teams} /></div><OverviewPane overview={overview} onGo={(t) => go(t as TabKey)} /></>)
                : hasOfficialRoles ? <Box><p className="acap" style={{ padding: 18 }}>{isOwn ? "You haven't played a recorded match yet. Your stats, teams and tournaments will appear here once you do." : `${fullName} hasn't played a recorded match yet.`}</p></Box>
                : <Box><div className="setup"><h3>{isOwn ? "Set up your profile" : "Nothing to show yet"}</h3><p>{isOwn ? "Choose how you take part: as a player, scorer, umpire or streamer. Your profile adapts to what you do." : `${fullName} hasn't played a match or set up a role yet.`}</p>{isOwn && <button className="btn primary" onClick={() => go("myroles")}>Choose your roles</button>}</div></Box>}
            </>)}
            {activeTab === "stats" && <StatsPane overview={overview} />}
            {activeTab === "analysis" && <AnalysisPane playerId={playerId} leftHanded={leftHanded} />}
            {activeTab === "matches" && <MatchesPane playerId={playerId} />}
            {activeTab === "teams" && <TeamsPane teams={teams} overview={overview} />}
            {activeTab === "records" && <RecordsPane overview={overview} />}
          </section>
        </main>
        {hasRail && (
        <aside className="rail" aria-label="Summary">
          {showSeasonRail && season && (
            <Box title="Latest season" sub={season.s} footer={<span style={{ fontSize: 12 }}>{season.tournaments} tournament{season.tournaments === 1 ? "" : "s"}</span>}>
              <div className="fsum">{[cell(season.m, "Matches"), cell(IN(season.r), "Runs"), cell(season.hs, "High score")]}</div>
              <div className="fsum" style={{ borderBottom: 0 }}>{[cell(season.w, "Wickets"), cell(batAll.h + batAll.f, "50+ scores (career)"), cell(batAll.h, "of them 100s")]}</div>
            </Box>
          )}
          {hasPlayerData && <Box title="Teams" right={<button className="link" onClick={() => go("teams")}>View all</button>}>
            {teams.length ? (<ul className="tlist">{teams.slice(0, 5).map((t: any, i: number) => (<li key={t.teamSnapshotId ?? i}><span className="crest" style={{ background: crestColor(t.teamName) }}>{initials(t.teamName)}</span><div><b>{t.teamName}</b><span className="m">{t.tournamentName ?? ""}</span></div></li>))}</ul>) : <p className="acap" style={{ padding: 16 }}>No teams yet.</p>}
          </Box>}
          {!hasPlayerData && hasOfficialRoles && <Box title="Roles"><ul className="tlist">{roleLabels.map((r) => <li key={r}><div><b>{r}</b><span className="m">Active</span></div></li>)}</ul></Box>}
        </aside>
        )}
      </div>
    </div>
    )}
    </>
  );
};
export default PlayerProfilePage;
